import React, { useState, useRef, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import {
  Chip,
  IconButton,
  Tooltip,
  Popover,
  Autocomplete,
} from "@/components/ui";
import { getTagColor } from "@/utils/tag-color";

interface TagManagerProps {
  /**
   * Current tags assigned to the resource
   */
  tags: string[];
  /**
   * All available tags for autocomplete suggestions
   */
  allTags?: { name: string }[];
  /**
   * Whether tags are currently being loaded/updated
   */
  isLoadingTags?: boolean;
  /**
   * Callback when tags are updated
   */
  onTagsChange: (tagNames: string[]) => Promise<void>;
  /**
   * Whether the component is in a compact mode
   */
  compact?: boolean;
  /**
   * Custom class name
   */
  className?: string;
  /**
   * Whether to show a tooltip on the add button
   */
  showTooltip?: boolean;
  /**
   * Maximum visible tags before showing count (for non-compact mode)
   */
  maxVisibleTags?: number;
}

/**
 * TagManager component for managing resource tags.
 * Displays current tags and provides a popover interface for adding/removing tags.
 * 
 * Features:
 * - Compact mode with responsive tag display
 * - Popover with autocomplete for tag management
 * - Color-coded tags
 * - Loading state handling
 * - Maintains popover position during updates
 */
const TagManager: React.FC<TagManagerProps> = ({
  tags = [],
  allTags = [],
  isLoadingTags = false,
  onTagsChange,
  compact = false,
  className = "",
  showTooltip = true,
  maxVisibleTags = 2,
}) => {
  const [tagPopoverAnchor, setTagPopoverAnchor] = useState<HTMLElement | null>(null);
  const [selectedTagNames, setSelectedTagNames] = useState<string[]>([]);
  const [isTagPopoverOpen, setIsTagPopoverOpen] = useState(false);
  const tagPopoverAnchorRef = useRef<HTMLElement | null>(null);
  const [anchorPosition, setAnchorPosition] = useState<{ top: number; left: number } | undefined>(undefined);

  // Initialize selectedTagNames with current tags
  useEffect(() => {
    setSelectedTagNames(tags);
  }, [tags]);

  // Maintain anchor reference
  useEffect(() => {
    if (tagPopoverAnchor) {
      tagPopoverAnchorRef.current = tagPopoverAnchor;
    }
  }, [tagPopoverAnchor]);

  // Sync popover open state
  useEffect(() => {
    setIsTagPopoverOpen(Boolean(tagPopoverAnchor || anchorPosition));
  }, [tagPopoverAnchor, anchorPosition]);

  const handleTagClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setSelectedTagNames(tags);
    const target = event.currentTarget;
    const rect = target.getBoundingClientRect();
    setTagPopoverAnchor(target);
    tagPopoverAnchorRef.current = target;
    setAnchorPosition({ top: rect.bottom, left: rect.left });
    setIsTagPopoverOpen(true);
  };

  const handleTagPopoverClose = () => {
    setTagPopoverAnchor(null);
    tagPopoverAnchorRef.current = null;
    setAnchorPosition(undefined);
    setIsTagPopoverOpen(false);
  };

  const handleTagsUpdate = async (newTagNames: string[]) => {
    setSelectedTagNames(newTagNames);

    // Save anchor position before update
    const currentAnchor = tagPopoverAnchorRef.current;
    let savedPosition: { top: number; left: number } | undefined = undefined;
    if (currentAnchor) {
      const rect = currentAnchor.getBoundingClientRect();
      savedPosition = { top: rect.bottom, left: rect.left };
    }

    await onTagsChange(newTagNames);

    // Restore anchor after update to prevent popover displacement
    if (currentAnchor && isTagPopoverOpen && savedPosition) {
      requestAnimationFrame(() => {
        let newAnchor: HTMLElement | null = null;

        // Try to reuse element if it still exists
        if (currentAnchor && document.body.contains(currentAnchor)) {
          newAnchor = currentAnchor;
        } else {
          // Otherwise, try to find the tag button
          const resourceCard = currentAnchor.closest(".resource-card");
          if (resourceCard) {
            const tagButton = resourceCard.querySelector("[data-tag-button]") as HTMLElement;
            if (tagButton) {
              newAnchor = tagButton;
              const newRect = tagButton.getBoundingClientRect();
              savedPosition = { top: newRect.bottom, left: newRect.left };
            }
          }
        }

        if (newAnchor) {
          setTagPopoverAnchor(newAnchor);
          tagPopoverAnchorRef.current = newAnchor;
          setAnchorPosition(undefined);
          setIsTagPopoverOpen(true);
        } else if (savedPosition) {
          setTagPopoverAnchor(null);
          setAnchorPosition(savedPosition);
          setIsTagPopoverOpen(true);
        }
      });
    }
  };

  const visibleTagsCount = compact ? 1 : maxVisibleTags;
  const remainingCount = tags.length - visibleTagsCount;

  const AddButton = (
    <IconButton
      variant="ghost"
      size="sm"
      onClick={handleTagClick}
      className={tags.length === 0 ? "w-4 h-4 p-0 text-content-tertiary" : "w-4 h-4 p-0"}
      data-tag-button
    >
      <FontAwesomeIcon icon={faPlus} className="text-[10px]" />
    </IconButton>
  );

  return (
    <>
      <div className={`flex items-center gap-1.5 flex-wrap ${className}`}>
        {tags.length > 0 ? (
          <>
            {/* Show tags based on visible count */}
            {tags.slice(0, visibleTagsCount).map((tag) => (
              <Chip
                key={tag}
                size="sm"
                variant="filled"
                style={{
                  backgroundColor: getTagColor(tag),
                  color: "white",
                }}
                className="text-xs"
              >
                {tag}
              </Chip>
            ))}
            
            {/* Remaining count */}
            {remainingCount > 0 && (
              <span className="text-xs text-content-tertiary">
                +{remainingCount}
              </span>
            )}
            
            {/* Add button */}
            {showTooltip ? (
              <Tooltip content="Gérer les tags">
                {AddButton}
              </Tooltip>
            ) : (
              AddButton
            )}
          </>
        ) : (
          showTooltip ? (
            <Tooltip content="Ajouter un tag">
              {AddButton}
            </Tooltip>
          ) : (
            AddButton
          )
        )}
      </div>

      {/* Popover for tag management */}
      <Popover
        open={isTagPopoverOpen}
        anchorEl={tagPopoverAnchor || tagPopoverAnchorRef.current}
        anchorPosition={anchorPosition}
        anchorReference={anchorPosition ? "anchorPosition" : "anchorEl"}
        onClose={handleTagPopoverClose}
        placement="bottom-start"
      >
        <div className="p-4 min-w-[300px] max-w-[400px]">
          <h4 className="mb-3 text-sm font-semibold text-content-primary">
            Gérer les tags
          </h4>
          <Autocomplete
            multiple
            freeSolo
            openOnFocus
            options={allTags.map((tag) => ({ label: tag.name, value: tag.name }))}
            value={selectedTagNames.map(name => ({ label: name, value: name }))}
            onChange={(newValue) => {
              const newTagNames = (newValue as any[]).map(item => 
                typeof item === 'string' ? item : item.label || item.value
              );
              handleTagsUpdate(newTagNames);
            }}
            placeholder="Sélectionner ou créer des tags..."
            renderTags={(value, getTagProps) =>
              value.map((item, index) => {
                const tagName = typeof item === 'string' ? item : (item as any).label || (item as any).value;
                return (
                  <Chip
                    key={tagName}
                    size="sm"
                    variant="filled"
                    style={{
                      backgroundColor: getTagColor(tagName),
                      color: "white",
                    }}
                    onDelete={getTagProps(index).onDelete}
                  >
                    {tagName}
                  </Chip>
                );
              })
            }
            renderOption={(option) => (
              <div className="flex items-center gap-2">
                <div
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: getTagColor(typeof option === 'string' ? option : (option as any).label) }}
                />
                <span>{typeof option === 'string' ? option : (option as any).label}</span>
              </div>
            )}
          />
        </div>
      </Popover>
    </>
  );
};

export default TagManager;